import { useState } from 'react';
import Seo from '@/components/Seo';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, Mail, MessageSquare, Loader2 } from 'lucide-react';
import { CONTACT } from '@/constants/testIds';
import { submitContact, extractErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { useAdSuppression } from '@/hooks/useAdSuppression';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

const TOPICS = ['General question', 'Report a bug', 'Feature request', 'Partnership', 'Other'];

// Simple but effective email regex: local@domain.tld with at least one dot in the domain.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactPage() {
    useAdSuppression();
    const [form, setForm] = useState({
        name: '',
        email: '',
        topic: 'General question',
        message: '',
        // Honeypot field. Named `hp_url_check` (matches backend) — an obscure
        // name that browser autofill / password managers won't recognise, so
        // real users' submissions don't get silently dropped.
        hp_url_check: '',
    });
    const [status, setStatus] = useState('idle'); // idle | sending | done | error
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        const name = form.name.trim();
        const email = form.email.trim();
        const message = form.message.trim();

        if (name.length < 1) {
            setError('Please enter your name.');
            setStatus('error');
            return;
        }
        if (!EMAIL_RE.test(email)) {
            setError('Please enter a valid email address (e.g. name@example.com).');
            setStatus('error');
            return;
        }
        if (message.length < 5) {
            setError('Please write a short message (at least a few words).');
            setStatus('error');
            return;
        }

        setStatus('sending');
        setError('');
        try {
            await submitContact({
                name,
                email,
                topic: form.topic,
                message,
                hp_url_check: form.hp_url_check,
            });
            setStatus('done');
            toast.success('Message sent. We\u2019ll reply within a few days.');
        } catch (err) {
            let msg = extractErrorMessage(err) || 'Something went wrong.';
            // Friendlier rate-limit message.
            if (err && err.response && err.response.status === 429) {
                msg = 'You\u2019ve sent a lot of messages in a short time. Please wait a minute and try again.';
            }
            setError(msg);
            setStatus('error');
            toast.error(msg);
        }
    };

    return (
        <>
            <Seo
                title="Contact"
                description="Get in touch with UtilityTool. Bug reports, feature requests, and partnership enquiries welcome. We reply personally."
                canonical="/contact"
            />
            <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16">
                <p className="text-sm text-slate-500">Contact</p>
                <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                    Say hello — we read every message.
                </h1>
                <p className="mt-2 text-slate-600 max-w-2xl">
                    Bug reports, feature requests, or just a friendly hello. We reply
                    personally, usually within a couple of business days.
                </p>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.3fr_0.7fr] gap-6">
                    <Card className="p-6 border-border/80">
                        {status === 'done' ? (
                            <div data-testid={CONTACT.success}>
                                <div className="flex items-center gap-2 text-emerald-700">
                                    <CheckCircle2 className="w-5 h-5" />
                                    <span className="font-semibold">Message sent</span>
                                </div>
                                <p className="mt-2 text-slate-600">
                                    Thanks, {form.name}. We’ve received your note about
                                    &ldquo;{form.topic}&rdquo; and will get back to you at
                                    <span className="font-medium text-slate-800"> {form.email}</span>.
                                </p>
                                <Button
                                    className="mt-6"
                                    variant="secondary"
                                    onClick={() => {
                                        setForm({ name: '', email: '', topic: 'General question', message: '', hp_url_check: '' });
                                        setStatus('idle');
                                    }}
                                >
                                    Send another message
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-4" noValidate>
                                {/* Honeypot: hidden field. Real users leave it blank; bots fill everything.
                                    Named `hp_url_check` (not `website`) so browser autofill / password
                                    managers don't fill it and cause false-positive bot detection. */}
                                <div
                                    aria-hidden="true"
                                    style={{
                                        position: 'absolute',
                                        left: '-10000px',
                                        top: 'auto',
                                        width: '1px',
                                        height: '1px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <label htmlFor="hp_url_check">Do not fill this field</label>
                                    <input
                                        id="hp_url_check"
                                        name="hp_url_check"
                                        type="text"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={form.hp_url_check}
                                        onChange={(e) => setForm({ ...form, hp_url_check: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label htmlFor="name">Your name</Label>
                                        <Input
                                            id="name"
                                            data-testid={CONTACT.formName}
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            placeholder="Jane Doe"
                                            className="mt-1.5"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            data-testid={CONTACT.formEmail}
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            placeholder="jane@example.com"
                                            className="mt-1.5"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="topic">Topic</Label>
                                    <Select
                                        value={form.topic}
                                        onValueChange={(v) => setForm({ ...form, topic: v })}
                                    >
                                        <SelectTrigger
                                            id="topic"
                                            className="mt-1.5"
                                            data-testid={CONTACT.formTopic}
                                        >
                                            <SelectValue placeholder="Choose a topic" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {TOPICS.map((t) => (
                                                <SelectItem key={t} value={t}>
                                                    {t}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="message">Message</Label>
                                    <Textarea
                                        id="message"
                                        data-testid={CONTACT.formMessage}
                                        value={form.message}
                                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                                        placeholder="Tell us how we can help…"
                                        rows={6}
                                        className="mt-1.5"
                                        required
                                    />
                                </div>
                                {error ? (
                                    <Alert variant="destructive">
                                        <AlertTitle>We couldn’t send your message</AlertTitle>
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                ) : null}
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <p className="text-xs text-slate-500">
                                        We reply personally. We don’t use auto-responders or share
                                        your email.
                                    </p>
                                    <Button
                                        type="submit"
                                        data-testid={CONTACT.submit}
                                        disabled={status === 'sending'}
                                        className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                                    >
                                        {status === 'sending' ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Sending…
                                            </>
                                        ) : (
                                            'Send message'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </Card>

                    <aside className="space-y-4">
                        <Card className="p-5 border-border/80">
                            <div className="flex items-center gap-2 text-slate-800">
                                <Mail className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Email us directly</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600">
                                Prefer email? Write to us at{' '}
                                <a href="mailto:hello@utilitytool.co.za" className="text-[hsl(var(--primary))] hover:underline">
                                    hello@utilitytool.co.za
                                </a>
                                .
                            </p>
                        </Card>
                        <Card className="p-5 border-border/80">
                            <div className="flex items-center gap-2 text-slate-800">
                                <MessageSquare className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Response time</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600">
                                We reply to most messages within 1–3 business days. Bug reports
                                get priority.
                            </p>
                        </Card>
                    </aside>
                </div>
            </section>
        </>
    );
}
